import { useState } from "react";
import { BarChart3, ShieldCheck } from "lucide-react";
import { useLocale } from "@/context/LanguageContext";
import { MutationErrorBanner } from "@/components/ui/MutationErrorBanner";
import { AnalyticsBreakdownDimension, AnalyticsGranularity } from "@/graphql/generated";
import {
  useAnalyticsBreakdown,
  useAnalyticsEvents,
  useAnalyticsTimeSeries,
  useAnalyticsTopEvents,
} from "../hooks/useAnalytics";
import { lastNDays } from "../utils/dateRange";
import { AnalyticsControls } from "./AnalyticsControls";
import { AnalyticsTimeSeriesChart } from "./AnalyticsTimeSeriesChart";
import { AnalyticsBreakdownChart } from "./AnalyticsBreakdownChart";
import { AnalyticsTopEvents } from "./AnalyticsTopEvents";
import { SegmentedToggle } from "./SegmentedToggle";

/** A Tier-1 event that always exists — safe default so charts have data on load. */
const DEFAULT_EVENT = "app_opened";
const TOP_LIMITS = [5, 10, 20, 50];

/** Fallback label for events without an explicit i18n entry (e.g. future events). */
function humanize(event: string): string {
  return event.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AnalyticsPage() {
  const { t } = useLocale();
  const initialRange = lastNDays(30);

  const [event, setEvent] = useState(DEFAULT_EVENT);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [granularity, setGranularity] = useState<AnalyticsGranularity>(AnalyticsGranularity.Daily);
  const [dimension, setDimension] = useState<AnalyticsBreakdownDimension>(
    AnalyticsBreakdownDimension.Platform,
  );
  const [topLimit, setTopLimit] = useState(10);

  const range = { from, to };

  const eventsQuery = useAnalyticsEvents();
  const events = eventsQuery.data?.analyticsEvents ?? [];

  const timeSeries = useAnalyticsTimeSeries({ event, range, granularity });
  const breakdown = useAnalyticsBreakdown({ event, range, dimension });
  const topEvents = useAnalyticsTopEvents({ range, limit: topLimit });

  // Localized event label, with a graceful fallback for any unlabeled event.
  const eventLabel = (ev: string) => {
    const key = `analytics.event.${ev}`;
    const label = t(key);
    return label === key ? humanize(ev) : label;
  };

  const granularityOptions = [
    { value: AnalyticsGranularity.Daily, label: t("analytics.granularity.daily") },
    { value: AnalyticsGranularity.Weekly, label: t("analytics.granularity.weekly") },
    { value: AnalyticsGranularity.Monthly, label: t("analytics.granularity.monthly") },
  ];
  const dimensionOptions = [
    { value: AnalyticsBreakdownDimension.Platform, label: t("analytics.dimension.platform") },
    { value: AnalyticsBreakdownDimension.AppVersion, label: t("analytics.dimension.appVersion") },
    { value: AnalyticsBreakdownDimension.Locale, label: t("analytics.dimension.locale") },
    { value: AnalyticsBreakdownDimension.DimValue, label: t("analytics.dimension.dimValue") },
  ];
  const dimensionValueHeader =
    dimensionOptions.find((option) => option.value === dimension)?.label ?? "";

  const firstError =
    timeSeries.error || breakdown.error || topEvents.error || eventsQuery.error;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <BarChart3 className="h-7 w-7" />
          {t("analytics.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("analytics.subtitle")}</p>
      </div>

      {/* k-anonymity notice */}
      <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-4 py-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">{t("analytics.kAnonBanner")}</p>
      </div>

      {firstError && <MutationErrorBanner message={firstError.message} />}

      <AnalyticsControls
        events={events.length > 0 ? events : [event]}
        event={event}
        onEventChange={setEvent}
        eventLabel={eventLabel}
        from={from}
        to={to}
        onRangeChange={(nextFrom, nextTo) => {
          setFrom(nextFrom);
          setTo(nextTo);
        }}
      />

      <AnalyticsTimeSeriesChart
        data={timeSeries.data?.analyticsTimeSeries ?? []}
        loading={timeSeries.loading}
        action={
          <SegmentedToggle
            ariaLabel={t("analytics.granularity.label")}
            value={granularity}
            onChange={setGranularity}
            options={granularityOptions}
          />
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <AnalyticsBreakdownChart
          data={breakdown.data?.analyticsBreakdown ?? []}
          loading={breakdown.loading}
          valueHeader={dimensionValueHeader}
          action={
            <SegmentedToggle
              ariaLabel={t("analytics.dimension.label")}
              value={dimension}
              onChange={setDimension}
              options={dimensionOptions}
            />
          }
        />
        <AnalyticsTopEvents
          data={topEvents.data?.analyticsTopEvents ?? []}
          loading={topEvents.loading}
          eventLabel={eventLabel}
          action={
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              {t("analytics.topEvents.limitLabel")}
              <select
                value={topLimit}
                onChange={(e) => setTopLimit(Number(e.target.value))}
                className="h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {TOP_LIMITS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          }
        />
      </div>
    </div>
  );
}
